import { useState, useEffect, useCallback } from "react";

// Storage key for filter state
const FILTER_STATE_KEY = "pay_share_filter_state";

export type PayShareFilterState = {
  selectedSession: string;
  selectedCourseId: number | null;
  selectedFeeType: string;
  selectedStage: string;
  selectedCenterId: number | null;
  recordsPerPage: number;
  currentPage: number;
};

export const usePayShareFilters = () => {
  // Get stored filter state from session storage
  const getStoredFilterState = (): PayShareFilterState | null => {
    const storedState = sessionStorage.getItem(FILTER_STATE_KEY);
    if (storedState) {
      try {
        return JSON.parse(storedState);
      } catch (e) {
        console.error("Error parsing stored filter state:", e);
      }
    }
    return null;
  };

  const storedState = getStoredFilterState();

  // State for filters
  const [selectedSession, setSelectedSession] = useState<string>(
    storedState?.selectedSession || ""
  );

  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(
    storedState?.selectedCourseId || null
  );

  const [selectedFeeType, setSelectedFeeType] = useState<string>(
    storedState?.selectedFeeType || ""
  );

  const [selectedStage, setSelectedStage] = useState<string>(
    storedState?.selectedStage || ""
  );

  const [selectedCenterId, setSelectedCenterId] = useState<number | null>(
    storedState?.selectedCenterId || null
  );

  const [recordsPerPage, setRecordsPerPage] = useState<number>(
    storedState?.recordsPerPage || 10
  );

  const [currentPage, setCurrentPage] = useState<number>(
    storedState?.currentPage || 1
  );

  // Store filter state in session storage whenever it changes
  useEffect(() => {
    const filterState = {
      selectedSession,
      selectedCourseId,
      selectedFeeType,
      selectedStage,
      selectedCenterId,
      recordsPerPage,
      currentPage,
    };
    sessionStorage.setItem(FILTER_STATE_KEY, JSON.stringify(filterState));
  }, [
    selectedSession,
    selectedCourseId,
    selectedFeeType,
    selectedStage,
    selectedCenterId,
    recordsPerPage,
    currentPage,
  ]);

  // Handler functions for filter changes
  const handleSessionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSession(e.target.value);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const courseId = Number(e.target.value);
    setSelectedCourseId(courseId);
    setCurrentPage(1);
  };

  const handleFeeTypeChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    feetypes: any[]
  ) => {
    setSelectedFeeType(e.target.value);

    // Get fee type by ID from API response
    const feeTypeId = e.target.value;
    const feeTypeObj = feetypes.find((ft) => ft.id.toString() === feeTypeId);
    const feeTypeCode = feeTypeObj?.type || "";

    // Reset stage selection if changing fee type
    if (feeTypeId === "2" || feeTypeCode === "registrationfee") {
      // Registration Fee
      setSelectedStage(""); // Don't default to any stage for Registration Fee
    } else {
      // Course Fee
      setSelectedStage(""); // Clear stage selection for Course Fee
    }
    setCurrentPage(1);
  };

  const handleStageChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedStage(e.target.value);
      setCurrentPage(1);
    },
    [setSelectedStage, setCurrentPage]
  );

  // Add center change handler
  const handleCenterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const centerId = Number(e.target.value);
    setSelectedCenterId(centerId);
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePaginationChange = (page: number, pageSize?: number) => {
    setCurrentPage(page);
    if (pageSize) setRecordsPerPage(pageSize);
  };

  // Update the filter state storage
  useEffect(() => {
    const filterState = {
      selectedSession,
      selectedCourseId,
      selectedFeeType,
      selectedStage,
      selectedCenterId,
      recordsPerPage,
      currentPage,
    };
    sessionStorage.setItem(FILTER_STATE_KEY, JSON.stringify(filterState));
  }, [
    selectedSession,
    selectedCourseId,
    selectedFeeType,
    selectedStage,
    selectedCenterId,
    recordsPerPage,
    currentPage,
  ]);

  // Update areAllFiltersSelected to include center check for superadmin
  const areAllFiltersSelected = (feetypes: any[], isSuperAdmin: boolean) => {
    // Get fee type code if available
    const feeTypeObj = feetypes.find(
      (ft) => ft.id.toString() === selectedFeeType
    );
    const feeTypeCode = feeTypeObj?.type || "";

    const baseFilters = selectedSession && selectedCourseId && selectedFeeType;
    const stageFilter =
      selectedFeeType === "2" ||
      feeTypeCode === "registrationfee" ||
      ((selectedFeeType === "1" || feeTypeCode === "coursefee") &&
        selectedStage);
    const centerFilter = !isSuperAdmin || selectedCenterId;

    return baseFilters && stageFilter && centerFilter;
  };

  // Update getMissingFiltersMessage to include center
  const getMissingFiltersMessage = (feetypes: any[], isSuperAdmin: boolean) => {
    const missingFilters = [];

    // Get fee type code if available
    const feeTypeObj = feetypes.find(
      (ft) => ft.id.toString() === selectedFeeType
    );
    const feeTypeCode = feeTypeObj?.type || "";

    if (!selectedSession) missingFilters.push("Session");
    if (!selectedCourseId) missingFilters.push("Course");
    if (!selectedFeeType) missingFilters.push("Fee Type");
    if (
      (selectedFeeType === "1" || feeTypeCode === "coursefee") &&
      !selectedStage
    )
      missingFilters.push("Stage");
    if (isSuperAdmin && !selectedCenterId) missingFilters.push("Center");
    return missingFilters.join(", ");
  };

  return {
    // Filter state
    selectedSession,
    selectedCourseId,
    selectedFeeType,
    selectedStage,
    selectedCenterId,
    recordsPerPage,
    currentPage,

    // Filter state setters
    setSelectedSession,
    setSelectedCourseId,
    setSelectedFeeType,
    setSelectedStage,
    setSelectedCenterId,
    setRecordsPerPage,
    setCurrentPage,

    // Handlers
    handleSessionChange,
    handleCourseChange,
    handleFeeTypeChange,
    handleStageChange,
    handleCenterChange,
    handlePaginationChange,

    // Helper functions
    getStoredFilterState,
    areAllFiltersSelected,
    getMissingFiltersMessage,
  };
};
